import { Component, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { AutoRedactionRule } from '../../../../models/auto-redaction-rule.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { AddAutoRedactionRuleComponent } from '../dialogs/add-auto-redaction-rule/add-auto-redaction-rule.component';
import { AutoRedactionJob } from '../../../../models/auto-redaction-job.model';
import { AutoRedactionService } from '../../../../services/auto-redaction-service/auto-redaction.service';
import { RunStatus } from '../../../../models/run-status.enum';
import { Group } from '../../../../models/group.model';
import { MatTableDataSource } from '@angular/material/table';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { ProcessingQueueRequest } from '../../../../models/processing-queue-request.model';
import { ProcessingQueueService } from '../../../../services/processing-queue.service';
import { forkJoin, Observable, of } from 'rxjs';
import { OkCancelData } from '../../../dialogs/ok-cancel/ok-cancel-dialog.model';
import { map, tap } from 'rxjs/operators';

@Component({
    selector: 'lex-auto-redaction-details',
    templateUrl: './auto-redaction-details.component.html',
    styleUrls: ['./auto-redaction-details.component.scss'],
    standalone: false
})
export class AutoRedactionDetailsComponent implements OnChanges, OnInit {
	@Input() autoRedactionJob: AutoRedactionJob;
	@Input() isGraphView: boolean;
	@Input() isProcessing$: Signal<boolean>;

	autoRedactionRulesDataSource: MatTableDataSource<AutoRedactionRule>;
	ruleColumns: string[] = ['ruleTitle', 'redactionLabel', 'rulePattern', 'presetType', 'id'];
	reportColumns: string[] = ['ruleTitle', 'docCount', 'redactionCount', 'id'];

	graphHeightByCount: number;
	paddingMultiplier: number;
	paddingConstant: number;
	chartGroups: Group[];
	RunStatus = RunStatus;

	private readonly undoRuleRedactionsFunctionId: string = 'f23de699-fd9a-4ba9-a137-57842776ea75';

	constructor(
		private dialog: MatDialog,
		private autoRedactionService: AutoRedactionService,
		private processingService: ProcessingQueueService,
		private router: Router
	) {
		this.autoRedactionRulesDataSource = new MatTableDataSource<AutoRedactionRule>([]);

	}
	ngOnInit() {
		this.autoRedactionRulesDataSource = new MatTableDataSource<AutoRedactionRule>(this.autoRedactionJob.autoRedactionRules);

	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.autoRedactionJob) {
			this.autoRedactionRulesDataSource.data = this.autoRedactionJob.autoRedactionRules;
		}
		if ((changes.autoRedactionJob || changes.isGraphView) && this.isGraphView && this.autoRedactionJob.autoRedactionRules) {
			this.chartGroups = [];
			this.autoRedactionJob.autoRedactionRules.filter(r => !r.isDeleted).forEach(rule => {
				const group = {
					name: rule.ruleTitle || rule.redactionLabel,
					series: [
						{
							name: 'Total Redactions',
							value: rule.redactionCount
						},
						{
							name: 'Documents Redacted',
							value: rule.docCount
						}
					]
				};

				this.chartGroups.push(group);
			});

			this.graphHeightByCount = this.chartGroups.length * 65;

			if (this.chartGroups.length < 4) {
				this.paddingMultiplier = 1 / (2 * this.chartGroups.length);
				this.paddingConstant = 0;
			} else {
				this.paddingMultiplier = 0;
				this.paddingConstant = 32;
			}
		}
	}

	computePadding(currentDivHeight: number) {
		const height = currentDivHeight > this.graphHeightByCount ? currentDivHeight : this.graphHeightByCount;
		const padding = (height * this.paddingMultiplier) + this.paddingConstant;
		return padding;
	}

	browseRule($event) {
		const rule = this.autoRedactionJob.autoRedactionRules.find(r => $event.series === r.ruleTitle || rule.redactionLabel);
		if (rule)
			this.router.navigate(['review/browse'], { queryParams: { AutoRedactionRuleIds: [rule.id] } });
	}

	deleteOrUndoRule(autoRedactionRuleId: string): void {
		this.deleteOrUndoRules([autoRedactionRuleId]).subscribe();
	}

	deleteOrUndoRules(autoRedactionRuleIds: string[]): Observable<boolean> {
		if (autoRedactionRuleIds.length == 0)
			return of(true);
		switch (this.autoRedactionJob.runStatus) {
			//delete
			case RunStatus.created:
				return forkJoin(autoRedactionRuleIds.map(rid => this.deleteRule(rid))).pipe(map(() => true));
				break;
			//error - do nothing
			case RunStatus.running:
				return this.showCantDeleteRunningOrWhileProcessing();
				break;
			//undo & delete
			case RunStatus.completed:
				if (this.isProcessing$())
					return this.showCantDeleteRunningOrWhileProcessing();
				else
					return this.undoAutoRedactionRules(autoRedactionRuleIds);
				break;
			//throw
			default:
				alert('error: Unexpected run status - ' + this.autoRedactionJob.runStatus);
				return of(false);
		}
	}

	private undoAutoRedactionRules(autoRedactionRuleIds: string[]): Observable<boolean> {
		const rules = this.autoRedactionRulesDataSource.data.filter(r => autoRedactionRuleIds.includes(r.id));
		let message = `Caution: This action will undo all auto-redactions applied by the following rules.` +
			`<br/> The redactions created by this rule will be permanently deleted from the case, this includes any automatic redactions that were later modified by a user.`
		message += rules.map(r => `<br/>* ${r.ruleTitle}: ${r.redactionCount} redactions`);
		const dialogConfig = {
			data: {
				title: `Undo Auto Redaction Rule`,
				message: message
			}
		} as MatDialogConfig<OkCancelData>;

		return this.showOkCancel(dialogConfig).pipe(map((okClicked: boolean) => {
			if (!okClicked)
				return false;

			rules.forEach(r => {
				const argument = JSON.stringify({ AutoRedactionRuleId: r.id });
				this.enqueueStep(this.undoRuleRedactionsFunctionId, argument).subscribe();
				this.deleteRule(r.id).subscribe();
			});

			return true;
		}));
	}

	private enqueueStep(functionId, functionArgument): Observable<boolean> {
		const step = new ProcessingQueueRequest();
		step.functionId = functionId;
		step.functionArgument = functionArgument;
		return this.processingService.addIncomingFunction(step);
	}

	private deleteRule(autoRedactionRuleId: string): Observable<boolean> {
		return this.autoRedactionService.deleteAutoRedactionRule(autoRedactionRuleId).pipe(tap((success: boolean) => {
			if (!success) return;
			this.autoRedactionRulesDataSource.data = this.autoRedactionJob.autoRedactionRules = this.autoRedactionJob.autoRedactionRules.filter(r => r.id !== autoRedactionRuleId);
		}));
	}

	showCantDeleteRunningOrWhileProcessing(): Observable<boolean> {
		return this.showOkCancel({
			data: {
				title: 'Warning: Unable to delete while running',
				message: '<ul><li> Cannot delete a rule that is running. </li><li> Cannot undo a rule while the case is processing.</li></ul>'
			}
		}).pipe(map(() => false));
	}

	private showOkCancel(config: MatDialogConfig<OkCancelData>): Observable<boolean> {
		return this.dialog.open(OkCancelDialogComponent, config).afterClosed();
	}

	generateNewRule(): AutoRedactionRule {
		const r = new AutoRedactionRule();
		r.autoRedactionJobId = this.autoRedactionJob.id;
		r.redactionColorId = '69b9cfdb-6f1a-4631-bb51-d1d5a30ce1aa';
		r.redactionLabel = 'Redacted';
		return r;
	}

	showAddRuleDialog(autoRedactionRule: AutoRedactionRule) {
		const dialogRef = this.dialog.open(AddAutoRedactionRuleComponent, {
			height: '441px',
			data: {
				existingRules: this.autoRedactionJob.autoRedactionRules.filter(r => r.id !== autoRedactionRule.id).map(srq => srq.rulePattern),
				autoRedactionRule: autoRedactionRule
			}
		});

		dialogRef.afterClosed().subscribe((rule: AutoRedactionRule) => {
			if (!rule)
				return;

			const idx = this.autoRedactionJob.autoRedactionRules.findIndex(r => r.id == rule.id);

			if (idx == -1)
				this.autoRedactionJob.autoRedactionRules.push(rule);
			else
				this.autoRedactionJob.autoRedactionRules[idx] = rule;

			this.autoRedactionRulesDataSource.data = this.autoRedactionJob.autoRedactionRules;
		});
	}
}
